/**
 * Exploración del drag & drop de la carga masiva (API webkitGetAsEntry).
 *
 * Las tres vías de entrada (DnD, selector de archivos, selector de carpeta)
 * producen entradas { file, relPath } donde relPath es la ruta de la CARPETA
 * contenedora relativa al punto de arrastre/selección (incluye la carpeta
 * superior arrastrada; '' para archivos sueltos). El modo plano la ignora;
 * el modo árbol la usa para replicar la estructura en el destino.
 *
 * Resiliencia: con árboles enormes (decenas de miles de archivos, unidades
 * sincronizadas tipo Google Drive/OneDrive) Chromium puede rechazar
 * readEntries con InvalidStateError ("state cached in an interface object
 * ... changed since it was read from disk") si el directorio cambia durante
 * la enumeración. Antes ese rechazo abortaba TODO el arrastre; ahora cada
 * directorio se enumera con reintentos (reader nuevo desde cero, sin
 * duplicados porque los archivos se resuelven después del drenado) y, si
 * agota los intentos, se salta SOLO ese directorio y se reporta en
 * failedDirs para avisar al usuario.
 */

const READ_ATTEMPTS = 3;          // intentos de enumerar un directorio
const RETRY_BASE_MS = 150;        // backoff entre intentos (150, 300)
const FILE_CHUNK = 200;           // resolución de File en paralelo por tandas

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const readEntriesAsync = (reader) =>
  new Promise((resolve, reject) => reader.readEntries(resolve, reject));

/**
 * Drena TODAS las entradas hijas de un directorio. Si readEntries falla a
 * mitad (InvalidStateError por cambio en disco), reintenta DESDE CERO con
 * un reader nuevo — como aún no se resolvió ningún File, no hay duplicados.
 */
async function readAllDirEntries(dirEntry) {
  let lastErr;
  for (let attempt = 1; attempt <= READ_ATTEMPTS; attempt++) {
    try {
      const reader = dirEntry.createReader();
      const all = [];
      while (true) {
        const batch = await readEntriesAsync(reader);
        if (!batch.length) return all;
        all.push(...batch);
      }
    } catch (e) {
      lastErr = e;
      if (attempt < READ_ATTEMPTS) await sleep(RETRY_BASE_MS * attempt);
    }
  }
  throw lastErr;
}

/**
 * @param {object}   entry       FileSystemEntry
 * @param {Array}    out         acumulador de { file, relPath }
 * @param {string}   dirPath     ruta de la carpeta contenedora
 * @param {function} [onTick]
 * @param {string[]} [failedDirs] directorios ilegibles tras los reintentos
 * @param {string[]} [dirsOut]   TODAS las carpetas visitadas, tengan archivos
 *                   o no. Es lo que permite crear las carpetas VACÍAS al subir
 *                   la estructura: derivar las rutas solo de los archivos las
 *                   dejaba fuera, porque no producen ninguna entrada.
 */
export async function traverseEntry(entry, out, dirPath = '', onTick, failedDirs = [], dirsOut = []) {
  if (!entry) return;
  if (entry.isFile) {
    await new Promise((resolve) => {
      try {
        entry.file(
          (f) => { out.push({ file: f, relPath: dirPath }); resolve(); },
          () => resolve()
        );
      } catch (e) {
        resolve();
      }
    });
    onTick?.(out.length);
    return;
  }
  if (entry.isDirectory) {
    const childPath = dirPath ? `${dirPath}/${entry.name}` : entry.name;
    dirsOut.push(childPath);
    let entries;
    try {
      entries = await readAllDirEntries(entry);
    } catch (e) {
      // Directorio ilegible tras los reintentos: se salta SOLO este y la
      // exploración del resto del árbol continúa.
      failedDirs.push(childPath);
      return;
    }
    const files = entries.filter((e) => e.isFile);
    const dirs = entries.filter((e) => e.isDirectory);
    // Los ARCHIVOS se resuelven en PARALELO por tandas (uno por uno tardaba
    // minutos con cientos de miles; miles simultáneos saturan el runtime).
    // Los subdirectorios se recorren en serie para no abrir miles de readers.
    for (let i = 0; i < files.length; i += FILE_CHUNK) {
      const chunk = files.slice(i, i + FILE_CHUNK);
      await Promise.all(chunk.map((fe) => new Promise((resolve) => {
        try {
          fe.file(
            (f) => { out.push({ file: f, relPath: childPath }); resolve(); },
            () => resolve()
          );
        } catch (e) {
          resolve();
        }
      })));
      onTick?.(out.length);
    }
    for (const sub of dirs) await traverseEntry(sub, out, childPath, onTick, failedDirs, dirsOut);
  }
}

/**
 * Explora un DataTransfer completo.
 * @returns {Promise<{files: Array<{file: File, relPath: string}>, dirs: string[], failedDirs: string[]}>}
 *   `dirs` trae TODAS las carpetas arrastradas, incluidas las vacías.
 */
export async function extractFilesFromDataTransfer(dt, onTick) {
  const out = [];
  const dirs = [];
  const failedDirs = [];
  if (dt?.items?.length) {
    // webkitGetAsEntry debe llamarse SIN awaits de por medio: el store del
    // DataTransfer se invalida al ceder el event loop.
    const entries = [];
    for (let i = 0; i < dt.items.length; i++) {
      const it = dt.items[i];
      if (it.kind !== 'file') continue;
      const entry = it.webkitGetAsEntry?.();
      if (entry) entries.push(entry);
      else { const f = it.getAsFile?.(); if (f) out.push({ file: f, relPath: '' }); }
    }
    for (const e of entries) await traverseEntry(e, out, '', onTick, failedDirs, dirs);
  } else if (dt?.files?.length) {
    for (let i = 0; i < dt.files.length; i++) out.push({ file: dt.files[i], relPath: '' });
  }
  return { files: out, dirs, failedDirs };
}
